import React from 'react';
import styled from 'styled-components';
import { IItemSourceMeanValidatorItemAiResult } from '../../../types/itemSourceMeanValidator';

type Props = { value?: IItemSourceMeanValidatorItemAiResult };

const ItemSourceMeanValidatorAiResultRenderer: React.FC<Props> = (params) => {
  const value = params.value;
  if (!value || typeof value !== 'object') return null;

  const decision = value.decision;
  const overallConfidence = value.confidence;
  const reasoning = value.reasoning;
  const modifiedTranslation = value.modified_translation;
  const errorMessage = value.error;

  const confidencePercent = (n?: number) => (typeof n === 'number' ? `${Math.round(n * 100)}%` : '');

  // Decision colors
  const decisionColor =
    decision === 'keep'
      ? '#4caf50'
      : decision === 'change'
      ? '#1565c0'
      : decision === 'error'
      ? '#d32f2f'
      : decision === 'exact_match'
      ? '#4caf50'
      : '#555';

  const truncatedReasoning = reasoning
    ? reasoning.length > 150
      ? reasoning.substring(0, 150) + '...'
      : reasoning
    : undefined;

  // Display logic based on decision type
  if (decision === 'keep') {
    return (
      <ResultContainer>
        <DecisionHeader>
          <DecisionText $color={decisionColor}>Keep</DecisionText>
          {typeof overallConfidence === 'number' && (
            <ConfidenceBadge $confidence={overallConfidence}>{confidencePercent(overallConfidence)}</ConfidenceBadge>
          )}
        </DecisionHeader>
        {truncatedReasoning && (
          <div
            style={{ color: '#666', fontSize: 11, lineHeight: 1.4, whiteSpace: 'normal', marginTop: 2 }}
            title={reasoning ?? ''}
          >
            {truncatedReasoning}
          </div>
        )}
      </ResultContainer>
    );
  }

  if (decision === 'change') {
    return (
      <ResultContainer>
        <DecisionHeader>
          <DecisionText $color={decisionColor}>Change</DecisionText>
          {typeof overallConfidence === 'number' && (
            <ConfidenceBadge $confidence={overallConfidence}>{confidencePercent(overallConfidence)}</ConfidenceBadge>
          )}
        </DecisionHeader>
        {modifiedTranslation && (
          <div style={{ fontSize: 12, color: '#333', marginTop: 4 }}>
            <strong>Modified:</strong> {modifiedTranslation}
          </div>
        )}
        {truncatedReasoning && (
          <div
            style={{ color: '#666', fontSize: 11, lineHeight: 1.4, whiteSpace: 'normal', marginTop: 2 }}
            title={reasoning ?? ''}
          >
            {truncatedReasoning}
          </div>
        )}
      </ResultContainer>
    );
  }

  if (decision === 'error') {
    return (
      <ResultContainer>
        <DecisionHeader>
          <DecisionText $color={decisionColor}>Error</DecisionText>
        </DecisionHeader>
        {errorMessage && (
          <div style={{ color: '#d32f2f', fontSize: 11, lineHeight: 1.4, whiteSpace: 'normal', marginTop: 2 }}>
            {errorMessage}
          </div>
        )}
      </ResultContainer>
    );
  }

  if (decision === 'exact_match') {
    return (
      <ResultContainer>
        <DecisionHeader>
          <DecisionText $color={decisionColor}>Exact Match</DecisionText>
        </DecisionHeader>
      </ResultContainer>
    );
  }

  return null;
};

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 1px 0;
`;

const DecisionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
`;

const DecisionText = styled.span<{ $color: string }>`
  color: ${(props) => props.$color};
  font-weight: 600;
  white-space: nowrap;
`;

const ConfidenceBadge = styled.span<{ $confidence: number }>`
  display: inline-flex;
  align-items: center;
  padding: 0 5px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  background-color: ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#e8f5e9';
    if (conf >= 0.6) return '#fff3e0';
    return '#ffebee';
  }};
  color: ${(props) => {
    const conf = props.$confidence;
    if (conf >= 0.8) return '#2e7d32';
    if (conf >= 0.6) return '#ef6c00';
    return '#c62828';
  }};
  border: 1px solid
    ${(props) => {
      const conf = props.$confidence;
      if (conf >= 0.8) return '#a5d6a7';
      if (conf >= 0.6) return '#ffcc80';
      return '#ef9a9a';
    }};
`;

export default ItemSourceMeanValidatorAiResultRenderer;